import range from 'lodash/range';
import PropTypes from 'prop-types';
import React from 'react';
import { injectIntl, FormattedMessage } from 'react-intl';
import cn from 'classnames';
import { paginationShape } from '@bibliocommons/bc-prop-types';
import { ChevronLeft, ChevronRight, DoubleChevronLeft, DoubleChevronRight } from '@bibliocommons/base-icons';

import PaginationItem from './PaginationItem';
import PaginationLabel from './PaginationLabel';
import './Pagination.scss';

function getNumbersRange(page, pages) {
  const potentialNumbers = [...range(Math.max(1, page - 7), page), ...range(page, Math.min(page + 7, pages) + 1)];

  const currentIndex = potentialNumbers.indexOf(page);
  const lastIndex = potentialNumbers.length - 1;
  const startIndex = Math.max(0, Math.min(lastIndex, currentIndex + 3) - 6);
  const endIndex = startIndex + 7;
  return potentialNumbers.slice(startIndex, endIndex);
}

export class Pagination extends React.PureComponent {
  renderEllipsis(number) {
    if (!this.props.compact) {
      return (
        <PaginationItem
          role="presentation"
          disabled={this.props.isFetching}
          key={`pagination-ellipsis-${number}`}
          router={this.props.router}
        >
          &hellip;
        </PaginationItem>
      );
    }

    return null;
  }

  renderPreviousChevron() {
    const { pagination, isFetching, handleClick, router } = this.props;
    const page = pagination.get('page');
    const previousDisabled = isFetching || page <= 1;

    return (
      <PaginationItem
        page={page - 1}
        disabled={previousDisabled}
        handleClick={handleClick}
        className="pagination__prev-chevron"
        key={`pagination-prev-chevron-${page}`}
        router={router}
      >
        <ChevronLeft />
      </PaginationItem>
    );
  }

  renderNextChevron() {
    const { pagination, isFetching, handleClick, router } = this.props;
    const page = pagination.get('page');
    const pages = pagination.get('pages');
    const nextDisabled = isFetching || page >= pages;

    return (
      <PaginationItem
        page={page + 1}
        disabled={nextDisabled}
        handleClick={handleClick}
        className="pagination__next-chevron"
        key={`pagination-next-chevron-${page}`}
        router={router}
      >
        <ChevronRight />
      </PaginationItem>
    );
  }

  renderFirstPageChevron() {
    if (this.props.showFirstPage) {
      const { pagination, isFetching, handleClick, router } = this.props;
      const previousDisabled = isFetching || pagination.get('page') <= 1;

      return (
        <PaginationItem
          page={1}
          disabled={previousDisabled}
          handleClick={handleClick}
          className="pagination__first-page-chevron"
          key="pagination-first-page-chevron"
          router={router}
        >
          <DoubleChevronLeft />
        </PaginationItem>
      );
    }

    return null;
  }

  renderLastPageChevron() {
    if (this.props.showLastPage) {
      const { pagination, isFetching, handleClick, router } = this.props;
      const page = pagination.get('page');
      const pages = pagination.get('pages');
      const nextDisabled = isFetching || page >= pages;

      return (
        <PaginationItem
          page={pages}
          disabled={nextDisabled}
          handleClick={handleClick}
          className="pagination__last-page-chevron"
          key="pagination-last-page-chevron"
          router={router}
        >
          <DoubleChevronRight />
        </PaginationItem>
      );
    }

    return null;
  }

  renderPageNumber(number) {
    if (!this.props.compact) {
      const { isFetching, pagination, handleClick, router } = this.props;

      return (
        <PaginationItem
          page={number}
          current={number === pagination.get('page')}
          disabled={isFetching}
          handleClick={handleClick}
          className="pagination__page-number"
          key={`pagination-page-number-${number}`}
          router={router}
        >
          <span className="pagination__page-number--desktop">{number}</span>
          <span className="pagination__page-number--mobile">
            <FormattedMessage id="pagination_page_number" values={{ page: number }} />
          </span>
        </PaginationItem>
      );
    }

    return null;
  }

  renderDesktopItems() {
    const { showFirstPage, pagination, showLastPage } = this.props;
    const page = pagination.get('page');
    const pages = pagination.get('pages');
    const pageNumbers = getNumbersRange(page, pages);
    const pageItems = pageNumbers.map(number => this.renderPageNumber(number));

    // Determine whether to provide a link to the first page.
    if (showFirstPage && page > 4) {
      pageItems[0] = this.renderPageNumber(1);
      pageItems[1] = this.renderEllipsis(1);
    }

    // Determine whether to provide a link to the last page.
    if (showLastPage && page <= pages - 4) {
      pageItems[pageItems.length - 1] = this.renderPageNumber(pages);
      pageItems[pageItems.length - 2] = this.renderEllipsis(pages);
    }

    pageItems.unshift(this.renderPreviousChevron());
    pageItems.push(this.renderNextChevron());

    return <ul className="pagination__desktop-items">{pageItems}</ul>;
  }

  renderMobileItems() {
    return (
      <ul className="pagination__mobile-items">
        {this.renderFirstPageChevron()}
        {this.renderPreviousChevron()}
        {this.renderPageNumber(this.props.pagination.get('page'))}
        {this.renderNextChevron()}
        {this.renderLastPageChevron()}
      </ul>
    );
  }

  render() {
    const { className, compact, dataKey, pagination, textKey, autoFocus, router, intl, hideLabel } = this.props;
    const classNames = cn('cp-pagination', className, {
      'pagination--compact': compact
    });

    return (
      <nav className={classNames} aria-label={intl.formatMessage({ id: 'pagination_label' })} data-key={dataKey}>
        {!hideLabel && (
          <PaginationLabel pagination={pagination} textKey={textKey} autoFocus={autoFocus} router={router} />
        )}
        {this.renderDesktopItems()}
        {this.renderMobileItems()}
      </nav>
    );
  }
}

Pagination.propTypes = {
  pagination: paginationShape.isRequired,
  className: PropTypes.string,
  dataKey: PropTypes.string,
  textKey: PropTypes.string,
  handleClick: PropTypes.func,
  autoFocus: PropTypes.bool,
  isFetching: PropTypes.bool,
  compact: PropTypes.bool,
  showFirstPage: PropTypes.bool,
  showLastPage: PropTypes.bool,
  router: PropTypes.bool,
  hideLabel: PropTypes.bool
};

Pagination.defaultProps = {
  dataKey: 'pagination-component',
  textKey: 'paginationInfo',
  router: true
};

export default injectIntl(Pagination);
